import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { RemindPreviewModal } from "@/components/dashboard/RemindPreviewModal";
import { SenderPicker, getSavedSender } from "@/components/dashboard/SenderPicker";
import { RecordPaymentDialog } from "@/components/dashboard/RecordPaymentDialog";
import { toast } from "sonner";
import { Printer, X, Mail, RefreshCcw, Pencil, Save, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }); } catch { return iso; }
};

// Company block comes from this workgroup's own Company profile (Settings ->
// Company) so Steeltooth / Jasper invoices never show Elite's details.
const useCompanyLines = () => {
  const [lines, setLines] = useState([]);
  useEffect(() => {
    api.get("/settings/company-profile").then((r) => {
      const p = r.data || {};
      let name = p.legal_name || "";
      if (p.trade_name) name = name ? `${name}, AKA ${p.trade_name}` : p.trade_name;
      const city = [p.city, p.province].filter(Boolean).join(" ") + (p.postal ? `  ${p.postal}` : "");
      const digits = [p.area_code, p.phone].filter(Boolean).join("").replace(/\D/g, "");
      const phone = digits.length === 10 ? `+1${digits}` : digits ? `+${digits}` : "";
      setLines([name, p.address_line1, p.address_line2, city, phone, p.email,
        p.hst_number ? `GST/HST Registration No.: ${p.hst_number}` : ""].filter(Boolean));
    }).catch(() => setLines([]));
  }, []);
  return lines;
};

// ---------------- Invoice view (template: circled areas of the sample invoice) ----------------
const InvoiceModal = ({ projectId, extraId, onClose }) => {
  const companyLines = useCompanyLines();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [sender, setSender] = useState(getSavedSender);
  const [toField, setToField] = useState(""); // editable recipient list (comma-separated)
  const [subjectField, setSubjectField] = useState(""); // typed subject — {tokens} allowed
  const [bodyField, setBodyField] = useState("");       // typed body — empty keeps the full invoice layout
  const [saveDefault, setSaveDefault] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [fields, setFields] = useState(null); // {invoiceNo, appNo, dueDate, subcontractDate, declDate, amount, po, pages, extraLines}
  const [lineItem, setLineItem] = useState("");   // composer for a new extra line
  const [lineDesc, setLineDesc] = useState("");

  // Editable invoice fields (Oct-13 request): due date, sub total, invoice
  // number, plus the CCDC 9B declaration page's application # and subcontract
  // date. Editing the sub total re-runs discount -> holdback -> HST -> total.
  const toISODate = (mmddyyyy) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(mmddyyyy || "");
    return m ? `${m[3]}-${m[1]}-${m[2]}` : "";
  };
  const toMMDDYYYY = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
    return m ? `${m[2]}/${m[3]}/${m[1]}` : "";
  };
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const startEdit = () => {
    if (!fields) {
      setFields({
        invoiceNo: data.next_invoice_no || "",
        appNo: data.stat_dec?.app_no ?? "",
        dueDate: toISODate(data.due_date),
        subcontractDate: data.subcontract_date || "",
        declDate: data.stat_dec?.dated_iso || "",
        amount: data.amount,
        po: data.po || "",
        pages: {
          coi: data.pages?.coi !== false,
          statDec: data.pages?.stat_dec !== false,
          wsib: data.pages?.wsib !== false,
        },
        extraLines: (data.extra_lines || []).map((l) => ({ item: l.item || "", description: l.description || "" })),
      });
    }
    setEditMode(true);
  };
  const addLine = () => {
    const item = lineItem.trim();
    const description = lineDesc.trim();
    if (!item && !description) return;
    setFields({ ...fields, extraLines: [...(fields.extraLines || []), { item, description }] });
    setLineItem("");
    setLineDesc("");
  };
  // the preview mirrors the backend math exactly (cents, rounded once each):
  // CRA rule — HST on (sub total - holdback); the 7% early-payment discount
  // comes off LAST and never touches the HST base
  const view = useMemo(() => {
    if (!data || !fields) return data;
    const amount = round2(fields.amount);
    const holdback = round2((amount * (data.holdback_pct || 0)) / 100);
    const net = round2(amount - holdback); // the HST base
    const hst = round2((net * (data.hst_pct || 0)) / 100);
    const seven = data.seven_pct_on ? round2(amount * 0.07) : 0;
    return {
      ...data, amount, seven_pct: seven, holdback, net, hst,
      total: round2(net + hst - seven),
      due_date: fields.dueDate ? toMMDDYYYY(fields.dueDate) : data.due_date,
      invoice_no: (fields.invoiceNo || "").trim() || data.next_invoice_no,
      po: fields.po,
      extra_lines: fields.extraLines || [],
    };
  }, [data, fields]); // eslint-disable-line
  const buildOverrides = () => {
    if (!fields) return undefined;
    const ov = {};
    if ((fields.invoiceNo || "").trim()) ov.invoice_no = fields.invoiceNo.trim();
    if (fields.dueDate) ov.due_date = fields.dueDate;
    ov.subcontract_date = fields.subcontractDate || ""; // "" clears the date baked into the scan
    if (fields.declDate) ov.declaration_date = fields.declDate; // the "dated" blank beside the application #
    if (fields.appNo !== "" && fields.appNo !== null) ov.app_no = Number(fields.appNo);
    if (fields.amount !== "" && fields.amount !== null) ov.amount = Number(fields.amount);
    ov.po = (fields.po || "").trim();
    ov.pages = { coi: !!fields.pages?.coi, stat_dec: !!fields.pages?.statDec, wsib: !!fields.pages?.wsib };
    ov.extra_lines = fields.extraLines || [];
    return ov;
  };

  // Save: persist the edited fields on the job so they prefill the next time
  // this invoice is opened (the backend also re-runs its validation). A saved
  // sub total only sticks to the project invoice — never to an extra's.
  const doSaveEdits = async () => {
    setSavingEdits(true);
    try {
      const payload = {
        invoice_no: fields.invoiceNo,
        due_date: fields.dueDate,
        app_no: fields.appNo,
        subcontract_date: fields.subcontractDate,
        declaration_date: fields.declDate,
        po: fields.po,
        pages: { coi: !!fields.pages?.coi, stat_dec: !!fields.pages?.statDec, wsib: !!fields.pages?.wsib },
        extra_lines: fields.extraLines || [],
      };
      if (!extraId) payload.amount = fields.amount;
      await api.put(`/projects/${projectId}/invoice-draft`, payload);
      toast.success("Invoice fields saved to this job — they'll prefill next time");
      setEditMode(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the invoice fields");
    } finally {
      setSavingEdits(false);
    }
  };

  useEffect(() => {
    setData(null);
    setErr(false);
    api
      .get(`/projects/${projectId}/invoice-data${extraId ? `?extra_id=${extraId}` : ""}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [projectId, extraId]);

  // opens the send window and prefills the subject/body from the saved
  // default template (falling back to the built-in token subject)
  const openSend = async () => {
    setToField(data?.customer_email || "");
    setConfirmSend(true);
    try {
      const r = await api.get("/settings/invoice-email-template");
      setSubjectField(r.data.subject || "Invoice #{invoice_no} — {job_name}");
      setBodyField(r.data.body || "");
    } catch {
      setSubjectField("Invoice #{invoice_no} — {job_name}");
      setBodyField("");
    }
  };

  const doSend = async () => {
    setSending(true);
    try {
      if (saveDefault) {
        try {
          await api.put("/settings/invoice-email-template", { subject: subjectField, body: bodyField });
        } catch {
          toast.warning("The default template wasn't saved — sending with these values anyway");
        }
      }
      const r = await api.post("/invoices/send", { project_id: projectId, extra_id: extraId || null, sender: sender || null, to: toField || null, overrides: buildOverrides(), subject: subjectField, body: bodyField });
      toast.success(`Invoice #${r.data.invoice_no} sent to ${r.data.to}`);
      if (r.data.qbo_po && !r.data.qbo_po_note) toast.success(`PO ${r.data.qbo_po} written to the QuickBooks invoice`);
      if (r.data.qbo_po_note) toast.warning(`Invoice sent, but the PO wasn't written to QuickBooks: ${r.data.qbo_po_note}`, { duration: 8000 });
      setConfirmSend(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invoice");
    } finally {
      setSending(false);
    }
  };

  // Print = the REAL backend PDF (invoice + insurance certificate + statutory
  // declaration), opened in the device's PDF viewer where it can be printed,
  // saved or shared. Falls back to printing the on-screen preview.
  const doPrint = async () => {
    setPrinting(true);
    try {
      const r = await api.post(`/projects/${projectId}/invoice.pdf`, { extra_id: extraId || null, overrides: buildOverrides() }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not build the PDF — printing the on-screen preview instead");
      window.print();
    } finally {
      setPrinting(false);
    }
  };

  const bt = view?.bill_to;
  const billToName = bt ? (bt.company_name || bt.name) : view?.customer;
  const attn = bt && bt.company_name && bt.name && bt.company_name.toLowerCase() !== bt.name.toLowerCase() ? bt.name : null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4 print:static print:overflow-visible print:bg-white print:p-0"
      data-testid="invoice-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Print only the invoice itself */}
      <style>{`@media print { body * { visibility: hidden; } .invoice-print, .invoice-print * { visibility: visible; } .invoice-print { position: absolute; left: 0; top: 0; width: 100%; } }`}</style>
      <div className="invoice-print mx-auto my-6 max-w-3xl rounded-lg bg-white p-6 shadow-2xl sm:p-8 print:my-0 print:rounded-none print:shadow-none">
        {!data ? (
          <p className="py-16 text-center text-sm text-gray-400">{err ? "Could not load invoice." : "Preparing invoice…"}</p>
        ) : (
          <>
            {/* header: logo + company block + INVOICE/DATE/DUE DATE */}
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="flex items-start gap-5">
                <img src="/api/branding/brand-logo" alt="Company logo" className="h-20 w-auto object-contain" data-testid="invoice-logo" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                <div className="text-sm leading-6 text-gray-800">
                  {companyLines.map((l, i) => (
                    <p key={i} className={i === 0 ? "font-bold" : ""}>{l}</p>
                  ))}
                </div>
              </div>
              <div className="w-52 shrink-0">
                <div className="bg-[#1a3fd1] px-4 py-2 text-center text-xl font-bold uppercase tracking-widest text-[#ffffff]">Invoice</div>
                <div className="mt-1 flex items-center justify-between bg-[#1a3fd1] px-4 py-1.5 text-[#ffffff]">
                  <span className="text-xs font-bold uppercase">Date</span>
                  <span className="text-sm" data-testid="invoice-date">{view.date}</span>
                </div>
                <div className="mt-1 flex items-center justify-between bg-[#1a3fd1] px-4 py-1.5 text-[#ffffff]">
                  <span className="text-xs font-bold uppercase">Due Date</span>
                  <span className="text-sm" data-testid="invoice-due-date">{view.due_date}</span>
                </div>
                <div className="mt-1 flex items-center justify-between border border-[#1a3fd1] px-4 py-1.5 text-[#1a3fd1]">
                  <span className="text-xs font-bold uppercase">Invoice #</span>
                  <span className="font-mono text-sm" data-testid="invoice-no">{view.invoice_no || view.next_invoice_no || "—"}</span>
                </div>
              </div>
            </div>

            {/* BILL TO */}
            <div className="mt-8">
              <p className="text-sm font-bold uppercase tracking-wide text-gray-900">Bill To</p>
              <div className="mt-1 text-sm leading-6 text-gray-800">
                <p className="font-semibold" data-testid="invoice-billto">{billToName || "—"}</p>
                {bt?.street_address && <p>{bt.street_address}</p>}
                {(bt?.city || bt?.province) && <p>{[bt?.city, bt?.province].filter(Boolean).join(", ")}</p>}
                {bt?.zip && <p>{bt.zip}</p>}
                {attn && <p>Attn: {attn}</p>}
              </div>
            </div>

            {/* P.O. NUMBER */}
            <div className="mt-6 border-t-4 border-[#1a3fd1] pt-3">
              <p className="text-sm font-bold uppercase tracking-wide text-gray-900">P.O. Number</p>
              <p className="mt-1 text-sm text-gray-800" data-testid="invoice-po">{view.po || "—"}</p>
            </div>

            {/* job block */}
            <div className="mt-6">
              <div className="h-2 bg-[#1a3fd1]" />
              <p className="mt-4 text-base font-semibold text-gray-900" data-testid="invoice-job">
                {data.title}{data.kind === "project" && data.location ? ` — ${data.location}` : ""}
              </p>
              {data.kind === "extra" && data.description && (
                <p className="mt-1 text-sm text-gray-700">{data.description}</p>
              )}
              {(view.extra_lines || []).map((ln, i) => (
                <div key={i} className="mt-2" data-testid={`invoice-line-${i}`}>
                  {ln.item && <p className="text-sm font-semibold text-gray-900">{ln.item}</p>}
                  {ln.description && <p className="text-sm text-gray-600">{ln.description}</p>}
                </div>
              ))}
            </div>

            {/* amounts */}
            <div className="mt-8 flex justify-end">
              <div className="w-72 space-y-1 text-sm">
                <div className="flex justify-between"><span>Amount</span><span className="font-mono" data-testid="invoice-amount">{money(view.amount)}</span></div>
                <div className="flex justify-between text-gray-600"><span>Less 10% holdback</span><span className="font-mono">-{money(view.holdback)}</span></div>
                <div className="flex justify-between text-gray-600"><span>HST (13%)</span><span className="font-mono">{money(view.hst)}</span></div>
                {Number(view.seven_pct) > 0 && (
                  <div className="flex justify-between text-gray-600" data-testid="invoice-seven-pct"><span>Less 7% early-payment discount</span><span className="font-mono">-{money(view.seven_pct)}</span></div>
                )}
                <div className="flex justify-between border-t-2 border-[#1a3fd1] pt-1 text-base font-bold">
                  <span>TOTAL DUE</span><span className="font-mono" data-testid="invoice-total">{money(view.total)}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* editable fields — write through to the printed PDF and the sent invoice */}
        {data && editMode && fields && (
          <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4 print:hidden" data-testid="invoice-edit-panel">
            <p className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-widest text-amber-700">
              Editing invoice fields — the preview, the printed PDF and the emailed invoice all use these values
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500" htmlFor="inv-edit-no">Invoice #</label>
                <input id="inv-edit-no" value={fields.invoiceNo} onChange={(e) => setFields({ ...fields, invoiceNo: e.target.value })}
                  data-testid="invoice-edit-invoice-no"
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-mono text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500" htmlFor="inv-edit-due">Due date</label>
                <input id="inv-edit-due" type="date" value={fields.dueDate} onChange={(e) => setFields({ ...fields, dueDate: e.target.value })}
                  data-testid="invoice-edit-due-date"
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500" htmlFor="inv-edit-amount">Sub total $</label>
                <input id="inv-edit-amount" type="number" min="0" step="0.01" value={fields.amount} onChange={(e) => setFields({ ...fields, amount: e.target.value })}
                  data-testid="invoice-edit-amount"
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-mono text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none" />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500" htmlFor="inv-edit-appno">Application # (declaration)</label>
                <input id="inv-edit-appno" type="number" min="1" step="1" value={fields.appNo} onChange={(e) => setFields({ ...fields, appNo: e.target.value })}
                  data-testid="invoice-edit-app-no"
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-mono text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500" htmlFor="inv-edit-subdate">Date of subcontract (declaration)</label>
                <input id="inv-edit-subdate" type="date" value={fields.subcontractDate} onChange={(e) => setFields({ ...fields, subcontractDate: e.target.value })}
                  data-testid="invoice-edit-subcontract-date"
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500" htmlFor="inv-edit-decldate">Date beside application #</label>
                <input id="inv-edit-decldate" type="date" value={fields.declDate} onChange={(e) => setFields({ ...fields, declDate: e.target.value })}
                  data-testid="invoice-edit-decl-date"
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-500" htmlFor="inv-edit-po">P.O. number</label>
                <input id="inv-edit-po" value={fields.po} onChange={(e) => setFields({ ...fields, po: e.target.value })}
                  data-testid="invoice-edit-po"
                  className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-mono text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none" />
              </div>
            </div>

            {/* which supplementary pages ship inside the PDF */}
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-gray-500">Pages in the PDF:</span>
              {[["coi", "Insurance certificate", "invoice-edit-page-coi"], ["statDec", "Statutory declaration", "invoice-edit-page-stat-dec"], ["wsib", "WSIB certificate", "invoice-edit-page-wsib"]].map(([key, label, tid]) => (
                <div key={key} className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600"
                  onClick={() => setFields({ ...fields, pages: { ...fields.pages, [key]: !fields.pages?.[key] } })}
                  data-testid={`${tid}-row`}>
                  <input
                    type="checkbox"
                    checked={!!fields.pages?.[key]}
                    readOnly
                    tabIndex={-1}
                    data-testid={tid}
                    className="pointer-events-none h-3.5 w-3.5 rounded border-gray-300 accent-[#1a3fd1]"
                  />
                  {label}
                </div>
              ))}
            </div>

            {/* extra item/description lines printed on the invoice page */}
            <div className="mt-3">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-gray-500">Extra lines on the invoice</span>
              {(fields.extraLines || []).map((ln, i) => (
                <div key={i} className="mt-1 flex items-center gap-2 rounded border border-gray-200 bg-white px-2.5 py-1.5" data-testid={`invoice-edit-line-${i}`}>
                  <span className="text-xs font-semibold text-gray-800">{ln.item || "—"}</span>
                  <span className="flex-1 truncate text-xs text-gray-500">{ln.description}</span>
                  <button
                    onClick={() => setFields({ ...fields, extraLines: fields.extraLines.filter((_, j) => j !== i) })}
                    data-testid={`invoice-edit-line-remove-${i}`}
                    className="text-gray-400 hover:text-red-600"
                    title="Remove this line"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <div className="mt-1 flex gap-2">
                <input
                  value={lineItem}
                  onChange={(e) => setLineItem(e.target.value)}
                  placeholder="Item"
                  data-testid="invoice-edit-line-item"
                  className="w-40 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none"
                />
                <input
                  value={lineDesc}
                  onChange={(e) => setLineDesc(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addLine()}
                  placeholder="Description"
                  data-testid="invoice-edit-line-desc"
                  className="flex-1 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none"
                />
                <button
                  onClick={addLine}
                  disabled={!lineItem.trim() && !lineDesc.trim()}
                  data-testid="invoice-edit-line-add"
                  title="Add this line to the invoice"
                  className="inline-flex items-center rounded-md bg-[#1a3fd1] px-3 py-1.5 text-[#ffffff] hover:bg-[#1633b8] disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-amber-700">
              Sub total edits re-run the holdback → HST → early-payment discount chain above (HST is on the sub total less holdback; the 7% discount never reduces HST). Page checkboxes and extra lines only change the printed/emailed PDF, not this preview's amounts. Save stores these values on the job so they prefill next time.
            </p>
          </div>
        )}

        <div className="mt-8 flex justify-end gap-2 print:hidden">
          {data && !editMode && (
            <button
              onClick={startEdit}
              data-testid="invoice-edit-toggle"
              title="Edit invoice fields, add item lines, and choose which PDF pages are included"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:border-[#1a3fd1] hover:text-[#1a3fd1]"
            >
              <Pencil className="h-3.5 w-3.5" /> Edit fields
            </button>
          )}
          {data && editMode && (
            <button
              onClick={doSaveEdits}
              disabled={savingEdits}
              data-testid="invoice-edit-save"
              title="Save these values to the job — they prefill the next time this invoice is opened"
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-emerald-700 disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" /> {savingEdits ? "Saving…" : "Save"}
            </button>
          )}
          <button
            onClick={openSend}
            data-testid="invoice-send-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-[#1633b8]"
          >
            <Mail className="h-3.5 w-3.5" /> Send
          </button>
          <button
            onClick={doPrint}
            disabled={printing || !data}
            data-testid="invoice-print-btn"
            title="Opens the full PDF — invoice + insurance certificate + statutory declaration"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-[#1a3fd1] disabled:opacity-50"
          >
            <Printer className="h-3.5 w-3.5" /> {printing ? "Building…" : "Print"}
          </button>
          <button
            onClick={onClose}
            data-testid="invoice-close-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:border-gray-400"
          >
            <X className="h-3.5 w-3.5" /> Close
          </button>
        </div>

        {/* send confirmation — shows the QuickBooks email before sending */}
        {confirmSend && data && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 print:hidden"
            data-testid="invoice-send-confirm"
            onClick={(e) => e.target === e.currentTarget && setConfirmSend(false)}
          >
            <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
              <p className="text-sm font-bold text-gray-900">Send this invoice?</p>
              <label className="mb-1 mt-3 block font-mono text-[10px] uppercase tracking-widest text-gray-500">From</label>
              <SenderPicker value={sender} onChange={setSender} testid="invoice-send-sender" />
              <p className="mt-3 text-xs text-gray-500">Email it to (edit or add more — separate multiple emails with commas):</p>
              <input
                value={toField}
                onChange={(e) => setToField(e.target.value)}
                placeholder="customer@example.com, pm@example.com"
                data-testid="invoice-send-email"
                className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none"
              />
              {!toField.trim() && (
                <p className="mt-2 text-xs text-amber-600">No email on file for this customer — type one or more addresses above.</p>
              )}
              <label className="mb-1 mt-3 block font-mono text-[10px] uppercase tracking-widest text-gray-500" htmlFor="inv-send-subject">Subject</label>
              <input
                id="inv-send-subject"
                value={subjectField}
                onChange={(e) => setSubjectField(e.target.value)}
                placeholder="Invoice #{invoice_no} — {job_name}"
                data-testid="invoice-send-subject"
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none"
              />
              <label className="mb-1 mt-3 block font-mono text-[10px] uppercase tracking-widest text-gray-500" htmlFor="inv-send-body">Email body</label>
              <textarea
                id="inv-send-body"
                value={bodyField}
                onChange={(e) => setBodyField(e.target.value)}
                rows={4}
                placeholder="Leave empty to include the full invoice layout. The invoice PDF is always attached."
                data-testid="invoice-send-body"
                className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-[#1a3fd1] focus:outline-none"
              />
              <p className="mt-1 text-[11px] text-gray-400">
                You can use {"{invoice_no} {job_name} {customer} {total} {due_date}"} — they fill in when the email goes out.
              </p>
              <div className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-gray-600" onClick={() => setSaveDefault(!saveDefault)} data-testid="invoice-send-save-default-row">
                <input
                  id="inv-send-save-default"
                  type="checkbox"
                  checked={saveDefault}
                  readOnly
                  tabIndex={-1}
                  data-testid="invoice-send-save-default"
                  className="pointer-events-none h-4 w-4 rounded border-gray-300 accent-[#1a3fd1]"
                />
                Save this subject and body as the default for future invoices
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setConfirmSend(false)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={doSend}
                  disabled={!toField.trim() || !sender || sending}
                  data-testid="invoice-send-confirm-btn"
                  className="rounded-md bg-[#1a3fd1] px-4 py-1.5 text-xs font-semibold text-[#ffffff] hover:bg-[#1633b8] disabled:opacity-40"
                >
                  {sending ? "Sending…" : "Send invoice"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------- Billing header stat card (mirrors the Home tab StatBox style) ----------------
const BillingStat = ({ label, value, sub, testid, tint, onClick, hint }) => {
  // Clickable cards render as <button> — browsers center button content
  // vertically, so force top-left stacking to match the other stat cards.
  const cls = `rounded-lg border p-4 text-left shadow-sm ${tint || "border-gray-200 bg-white"}${onClick ? " flex w-full flex-col cursor-pointer transition-shadow hover:shadow-md" : ""}`;
  const inner = (
    <>
      <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="mt-1 font-mono text-[11px] text-gray-500">{sub}</p>}
      {hint && <p className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-[#1a3fd1]/70">{hint}</p>}
    </>
  );
  return onClick ? (
    <button onClick={onClick} data-testid={testid} className={cls}>{inner}</button>
  ) : (
    <div data-testid={testid} className={cls}>{inner}</div>
  );
};

// ---------------- Billed card — current year headline; every year row drills into that year's audit list ----------------
const BilledCard = ({ stats, onOpen }) => {
  const years = stats.billed_years || [];
  const cur = years[0] || { year: new Date().getFullYear(), total: 0, count: 0 };
  const prev = years.slice(1);
  return (
    <div
      data-testid="billing-stat-billed"
      className="flex flex-col rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-left shadow-sm transition-shadow hover:shadow-md"
    >
      <button onClick={() => onOpen(cur.year)} data-testid="billing-stat-billed-current" className="text-left">
        <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Billed {cur.year}</p>
        <p className="mt-1 text-2xl font-bold text-gray-900">{money(cur.total)}</p>
        <p className="mt-1 font-mono text-[11px] text-gray-500">{cur.count} invoice{cur.count === 1 ? "" : "s"} this year</p>
      </button>
      {prev.length > 0 && (
        <div className="mt-auto space-y-1 border-t border-emerald-200/70 pt-2">
          {prev.map((y) => (
            <button
              key={y.year}
              onClick={() => onOpen(y.year)}
              data-testid={`billed-line-${y.year}`}
              title={`View ${y.year} invoices`}
              className="flex w-full items-baseline justify-between gap-2 rounded px-1 -mx-1 transition-colors hover:bg-emerald-100/70"
            >
              <span className="font-mono text-[11px] text-gray-500">{y.year}</span>
              <span className="truncate font-mono text-[11px] font-semibold text-gray-700">
                {money(y.total)} · {y.count} inv
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------- Next payments card — lists the next 3 due dates; click opens the receivables detail ----------------
const NextPaymentsCard = ({ stats, onOpen }) => {
  const nps = stats.next_payments || [];
  const today = new Date().toISOString().slice(0, 10);
  return (
    <button
      onClick={onOpen}
      data-testid="billing-stat-next-payment"
      className="rounded-lg border border-[#1a3fd1]/30 bg-[#1a3fd1]/5 p-4 text-left shadow-sm transition-shadow hover:shadow-md"
    >
      <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Next payments due</p>
      {nps.length === 0 ? (
        <>
          <p className="mt-1 text-2xl font-bold text-gray-900">—</p>
          <p className="mt-1 font-mono text-[11px] text-gray-500">no outstanding due dates</p>
        </>
      ) : (
        <ul className="mt-1.5 space-y-1">
          {nps.map((np) => (
            <li key={np.date} className="flex items-baseline justify-between gap-2" data-testid={`next-payment-${np.date}`}>
              <span className={`font-mono text-sm font-semibold ${np.date < today ? "text-red-600" : "text-gray-900"}`}>
                {fmtDate(np.date)}{np.date < today ? " · overdue" : ""}
              </span>
              <span className="font-mono text-sm font-bold text-gray-900">{money(np.amount)}</span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-[#1a3fd1]/70">Click for who owes what</p>
    </button>
  );
};

// ---------------- Receivables detail — which companies owe what (from synced QBO invoices) ----------------
const ReceivablesModal = ({ stats, onClose, onReminded, focusCustomer, onClearFocus }) => {
  const all = stats.receivables || [];
  const rows = focusCustomer ? all.filter((r) => (r.customer || "").toLowerCase() === focusCustomer.toLowerCase()) : all;
  const [remindFor, setRemindFor] = useState(null); // customer name in the preview window
  const [history, setHistory] = useState(null); // null = loading
  const loadHistory = useCallback(() => {
    api.get("/billing/reminders", { params: { limit: 10 } })
      .then((r) => setHistory(r.data.rows || []))
      .catch(() => setHistory([]));
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      data-testid="receivables-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1a3fd1]">Receivables</p>
            <h3 className="flex items-center gap-2 text-sm font-bold text-gray-900">
              {(focusCustomer && rows.length ? rows[0].customer : focusCustomer) || "Who owes what"}
              {focusCustomer && (
                <button
                  onClick={onClearFocus}
                  data-testid="receivables-show-all"
                  className="rounded-full border border-gray-300 px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider text-gray-500 hover:bg-gray-50"
                >
                  All customers
                </button>
              )}
            </h3>
          </div>
          <button onClick={onClose} data-testid="receivables-close" className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-500 hover:text-gray-800">
            <X className="h-4 w-4" />
          </button>
        </div>
        {rows.length === 0 ? (
          <p className="py-6 text-center text-xs text-gray-400">Nothing outstanding — every synced invoice is paid.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {rows.map((r) => (
              <div key={r.customer} className="rounded-md border border-gray-200 px-3 py-2" data-testid={`receivable-${r.customer}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-semibold text-gray-800">{r.customer}</p>
                  <div className="flex shrink-0 items-center gap-2">
                    <p className="font-mono text-sm font-bold text-amber-700">{money(r.amount)}</p>
                    <button
                      onClick={() => setRemindFor(r.customer)}
                      data-testid={`remind-${r.customer}`}
                      title="Preview and send an overdue reminder"
                      className="inline-flex items-center gap-1 rounded bg-[#1a3fd1] px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-[#1633b8]"
                    >
                      <Mail className="h-3 w-3" /> Remind
                    </button>
                  </div>
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
                  {r.invoice_count} unpaid invoice{r.invoice_count === 1 ? "" : "s"}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {r.invoices.map((inv) => (
                    <li key={inv.doc_number} className="flex items-center justify-between font-mono text-[11px] text-gray-500">
                      <span>#{inv.doc_number}{inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ""}{inv.last_reminder_at ? ` · reminded ${fmtDate(inv.last_reminder_at)}` : ""}</span>
                      <span className="font-semibold text-gray-700">{money(inv.balance)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex items-center justify-between border-t border-gray-200 pt-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Total outstanding</p>
          <p className="font-mono text-sm font-bold text-gray-900" data-testid="receivables-total">{money(focusCustomer ? rows.reduce((s, r) => s + r.amount, 0) : stats.amount_due)}</p>
        </div>
        {remindFor && (
          <RemindPreviewModal
            customer={remindFor}
            onClose={() => setRemindFor(null)}
            onSent={() => { setRemindFor(null); loadHistory(); onReminded?.(); }}
          />
        )}
        {history && history.length > 0 && (
          <div className="mt-3 border-t border-gray-200 pt-3">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-gray-400">Recent reminders</p>
            <ul className="space-y-1" data-testid="reminder-history">
              {history.map((h, i) => (
                <li key={i} className="flex items-center justify-between gap-2 font-mono text-[11px] text-gray-500" data-testid={`reminder-log-${i}`}>
                  <span className="truncate">{h.customer} <span className="text-gray-400">→ {h.to}</span></span>
                  <span className="shrink-0">{fmtSyncTime(h.sent_at)} · {h.invoice_count} inv · {money(h.total)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ---------------- "Last synced from QuickBooks" timestamp ----------------
const fmtSyncTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const t = d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
  return new Date().toDateString() === d.toDateString() ? t : `${fmtDate(iso.slice(0, 10))}, ${t}`;
};

// ---------------- QBO-synced invoice row (standalone, pulled from QuickBooks) ----------------
const QboInvoiceRow = ({ r, onOpen, onMarkedPaid }) => {
  const overdue = r.due_date && Number(r.balance) > 0 && new Date(r.due_date) < new Date();
  const [payOpen, setPayOpen] = useState(false);
  const inner = (
    <>
      <div className="min-w-0">
        <p className="flex items-center gap-2 truncate text-sm font-semibold text-gray-800">
          <span className="truncate">{r.project_id ? r.name : `Invoice #${r.doc_number || r.qbo_id}`}</span>
          <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-emerald-700">QBO</span>
        </p>
        <p className="truncate text-xs text-gray-500">
          {r.project_id ? `${r.customer || "—"} · ` : ""}#{r.doc_number || r.qbo_id} · {fmtDate(r.txn_date)}
          {r.due_date && (
            <span className={overdue ? "font-semibold text-red-600" : ""}> · due {fmtDate(r.due_date)}</span>
          )}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-mono text-sm font-bold text-emerald-800">{money(r.total)}</p>
        {Number(r.balance) > 0 ? (
          <p className={`font-mono text-[10px] font-semibold uppercase tracking-wider ${overdue ? "text-red-600" : "text-amber-600"}`} data-testid={`qbo-balance-${r.qbo_id}`}>{money(r.balance)} due</p>
        ) : (
          <p className="font-mono text-[10px] font-semibold uppercase tracking-wider text-emerald-600" data-testid={`qbo-paid-${r.qbo_id}`}>Paid{r.paid_date ? ` ${fmtDate(r.paid_date)}` : ""}</p>
        )}
      </div>
    </>
  );
  // NOTE: no w-full here — combined with flex-1 it makes the card claim 100%
  // of the row and pushes the "Mark paid" sibling past the card edge on
  // narrow screens. flex-1 + min-w-0 sizes it to the space actually left.
  const cls = "flex min-w-0 flex-1 items-center justify-between gap-3 rounded-md border border-emerald-200 bg-white px-3 py-2.5 text-left transition-shadow hover:shadow-md";
  const main = onOpen ? (
    <button onClick={onOpen} data-testid={`invoice-sent-qbo-${r.qbo_id}`} className={cls}>{inner}</button>
  ) : (
    <a href={`https://qbo.intuit.com/app/invoice?txnId=${r.qbo_id}`} target="_blank" rel="noopener noreferrer" data-testid={`invoice-sent-qbo-${r.qbo_id}`} className={cls}>{inner}</a>
  );
  return (
    <div className="flex items-stretch gap-2">
      {main}
      {Number(r.balance) > 0 && (
        <button
          onClick={() => setPayOpen(true)}
          data-testid={`mark-paid-${r.qbo_id}`}
          title="Record a full or partial payment in QuickBooks"
          className="shrink-0 self-center whitespace-nowrap rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-700 transition-colors hover:bg-emerald-100"
        >
          Mark paid
        </button>
      )}
      {payOpen && (
        <RecordPaymentDialog
          invoice={r}
          onClose={() => setPayOpen(false)}
          onRecorded={() => { setPayOpen(false); onMarkedPaid?.(); }}
        />
      )}
    </div>
  );
};

const BillingTab = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState({ to_be_invoiced: [], invoices_sent: [] });
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null); // { projectId, extraId? }
  const [confirmAll, setConfirmAll] = useState(false);
  const [senderAll, setSenderAll] = useState(getSavedSender);
  const [sendingAll, setSendingAll] = useState(false);
  const [sortBy, setSortBy] = useState("recent");
  const [syncing, setSyncing] = useState(false);
  // Trash-can delete of app-generated invoice records (superadmin only —
  // QBO rows never get one). deleteTarget = the invoices_sent row pending
  // confirmation.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Deep link: ?tab=billing&receivable=<customer> opens Who-owes-what
  // focused on that customer (from the Owes chips on the Projects tab).
  const [receivablesFocus, setReceivablesFocus] = useState(null);
  useEffect(() => {
    const cust = searchParams.get("receivable");
    if (cust) {
      setReceivablesFocus(cust);
      setReceivablesOpen(true);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("tab", "billing");
          p.delete("receivable");
          return p;
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    try {
      const o = await api.get("/billing/overview");
      setOverview(o.data);
    } catch { /* leave buckets empty */ } finally {
      setLoading(false);
    }
  }, []);

  // Trash-can delete: un-bills every app-side labour/material/extra record
  // for the project (QBO invoices are never touched — they don't get a
  // trash can at all).
  const doDeleteAppInvoice = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/billing/app-invoices/${deleteTarget.project_id}`);
      const d = r.data || {};
      toast.success(
        `Deleted — ${d.labour_unbilled || 0} labour + ${d.expenses_unbilled || 0} material${(d.extras_unbilled || 0) ? ` + ${d.extras_unbilled} extra(s)` : ""} went back to unbilled`,
      );
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete the app invoice records");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const syncQbo = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/billing/sync-qbo-invoices");
      toast.success(`Synced ${r.data.synced} invoice${r.data.synced === 1 ? "" : "s"} from QuickBooks`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "QuickBooks sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // Project rows (lifetime draw aggregates) have no single QBO invoice to
  // settle, so they carry an app-local paid flag the owner can flip.
  const toggleProjectPaid = async (r) => {
    try {
      await api.post(`/projects/${r.project_id}/billing-paid`, { paid: !r.paid });
      toast.success(r.paid ? `${r.name} marked unpaid` : `${r.name} marked paid`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the paid flag");
    }
  };

  // WSIB clearance — one current certificate at a time; the newest upload
  // replaces the old one and rides as the last page of every invoice PDF.
  // Images load through axios (Bearer token) because Android PWAs evict
  // cookies — a plain <img src> would randomly 401 on the shop phones.
  const [wsib, setWsib] = useState(null);
  const [wsibThumb, setWsibThumb] = useState("");
  const [wsibBusy, setWsibBusy] = useState(false);
  const wsibFileRef = useRef(null);
  const loadWsib = async () => {
    try {
      const r = await api.get("/company/wsib");
      setWsib(r.data);
      if (r.data?.has_image) {
        const t = await api.get(`/company/wsib/thumb?v=${encodeURIComponent(r.data.uploaded_at || "")}`, { responseType: "blob" });
        setWsibThumb((old) => {
          if (old) URL.revokeObjectURL(old);
          return URL.createObjectURL(t.data);
        });
      } else {
        setWsibThumb("");
      }
    } catch { /* leave the card empty */ }
  };
  useEffect(() => { loadWsib(); }, []); // eslint-disable-line
  const uploadWsib = async (file) => {
    if (!file) return;
    setWsibBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/company/wsib", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadWsib();
      toast.success("WSIB clearance updated — it goes on every invoice from now on");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "WSIB upload failed");
    } finally {
      setWsibBusy(false);
      if (wsibFileRef.current) wsibFileRef.current.value = "";
    }
  };
  const viewWsib = async () => {
    try {
      const r = await api.get(`/company/wsib/image?v=${encodeURIComponent(wsib?.uploaded_at || "")}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "image/jpeg" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open the WSIB image");
    }
  };

  // Year filter for Invoices sent — current year by default, "- All -" or
  // any previous year selectable. Years are only offered when they actually
  // have invoices in the CURRENT view: unpaid years by default; every year
  // once "show paid" expands the list.
  const [sentYear, setSentYear] = useState(String(new Date().getFullYear()));
  const [showPaid, setShowPaid] = useState(false);
  const sentYears = useMemo(() => {
    const ys = new Set();
    for (const r of overview.invoices_sent || []) {
      if (!showPaid && r.paid) continue; // hidden rows don't qualify their year
      const y = (r.txn_date || r.last_billed_at || "").slice(0, 4);
      if (y) ys.add(y);
    }
    return [...ys].sort().reverse();
  }, [overview, showPaid]);
  // If the selected year no longer has any visible invoices (e.g. after
  // collapsing paid rows), fall back to the newest available year.
  useEffect(() => {
    if (sentYear !== "all" && sentYears.length > 0 && !sentYears.includes(sentYear)) {
      setSentYear(sentYears[0]);
    }
  }, [sentYears, sentYear]);
  const yearFilteredSent = useMemo(() => {
    const rows = overview.invoices_sent || [];
    if (sentYear === "all") return rows;
    return rows.filter((r) => (r.txn_date || r.last_billed_at || "").startsWith(sentYear));
  }, [overview, sentYear]);

  const sortedSent = useMemo(() => {
    const rows = [...yearFilteredSent];
    const dueVal = (r) => r.due_date || "9999-12-31"; // no due date sorts last
    if (sortBy === "due") rows.sort((a, b) => dueVal(a).localeCompare(dueVal(b)));
    else if (sortBy === "date") rows.sort((a, b) => (b.txn_date || b.last_billed_at || "").localeCompare(a.txn_date || a.last_billed_at || ""));
    else if (sortBy === "amount") rows.sort((a, b) => (b.billed_total ?? b.total ?? 0) - (a.billed_total ?? a.total ?? 0));
    else rows.sort((a, b) => (b.last_billed_at || "").localeCompare(a.last_billed_at || ""));
    return rows;
  }, [yearFilteredSent, sortBy]);

  // Invoices sent shows UNPAID rows only by default; "show more" expands to
  // include paid invoices. (showPaid state lives with the year filter above.)
  const paidSentCount = useMemo(() => sortedSent.filter((r) => r.paid).length, [sortedSent]);
  const visibleSent = useMemo(
    () => (showPaid ? sortedSent : sortedSent.filter((r) => !r.paid)),
    [sortedSent, showPaid]
  );
  const hiddenPaidCount = sortedSent.length - visibleSent.length;

  // Both billing boxes start collapsed to the first 3 rows; a show more /
  // show less button expands and collapses them.
  const [sentExpanded, setSentExpanded] = useState(false);
  const cappedSent = sentExpanded ? visibleSent : visibleSent.slice(0, 3);
  const [tbiExpanded, setTbiExpanded] = useState(false);
  const tbiRows = overview.to_be_invoiced || [];
  const cappedTbi = tbiExpanded ? tbiRows : tbiRows.slice(0, 3);

  // Invoice audit report — flat list of every invoice (app-sent + QBO),
  // filterable by month for the accountant.
  const [auditYear, setAuditYear] = useState(String(new Date().getFullYear()));
  const [auditMonthNum, setAuditMonthNum] = useState(""); // "" = whole year
  const [audit, setAudit] = useState(null); // null = loading
  const [auditExpanded, setAuditExpanded] = useState(false); // collapsed: 3 rows
  const [auditMonthsAvail, setAuditMonthsAvail] = useState([]); // months that actually have invoices in the selected year
  const [receivablesOpen, setReceivablesOpen] = useState(false);

  // Clicking a Billed-year card drills into the audit list for that year.
  const auditRef = useRef(null);
  const drillToAuditYear = (year) => {
    setAuditYear(String(year));
    setAuditMonthNum("");
    requestAnimationFrame(() => auditRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  // Audit year options = years in the data + the drilled-into year (so the
  // dropdown always displays the active filter even when that year is empty)
  const auditYearOptions = useMemo(() => {
    const ys = new Set(sentYears);
    if (auditYear !== "all") ys.add(auditYear);
    return [...ys].sort().reverse();
  }, [sentYears, auditYear]);

  useEffect(() => {
    let live = true;
    setAudit(null);
    setAuditExpanded(false);
    const params = {};
    if (auditYear !== "all") {
      if (auditMonthNum) params.month = `${auditYear}-${auditMonthNum}`;
      else params.year = auditYear;
    }
    api
      .get("/billing/invoice-audit", { params })
      .then((r) => {
        if (!live) return;
        setAudit(r.data);
        setAuditMonthsAvail(r.data.months || []);
      })
      .catch(() => { if (live) { setAudit({ rows: [], count: 0 }); setAuditMonthsAvail([]); } });
    return () => { live = false; };
  }, [auditYear, auditMonthNum]);

  // If the selected month has no invoices in the newly selected year, reset it
  useEffect(() => {
    if (auditMonthNum && auditYear !== "all" && audit !== null && !auditMonthsAvail.includes(auditMonthNum)) {
      setAuditMonthNum("");
    }
  }, [auditMonthsAvail, auditMonthNum, auditYear, audit]);

  const invoiceAll = async () => {
    setSendingAll(true);
    try {
      const r = await api.post("/invoices/send-all", { sender: senderAll || null });
      if (r.data.sent > 0) toast.success(`Sent ${r.data.sent} of ${r.data.total} invoices`);
      const fails = r.data.results.filter((x) => !x.ok);
      if (fails.length) toast.error(`Skipped ${fails.length}: ${fails.map((f) => f.label).join(", ")}`);
      setConfirmAll(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invoices");
    } finally {
      setSendingAll(false);
    }
  };

  return (
    <div className="bg-[#f4f5f8] p-4 sm:p-6" data-testid="billing-tab">
      <div className="mx-auto mb-6 flex max-w-[1400px] items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#4f6fe8]">Finance</p>
          <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Billing</h1>
        </div>
        {/* WSIB clearance — tap the thumbnail to view it full size; the
            newest upload replaces the old one and is appended to every
            invoice PDF automatically. */}
        <div className="flex shrink-0 flex-col items-center gap-1 rounded-md border border-[#e3e7ef] bg-white p-2 shadow-sm" data-testid="wsib-card">
          {wsib?.has_image && wsibThumb ? (
            <button
              onClick={viewWsib}
              data-testid="wsib-view-link"
              title="View the current WSIB clearance full size"
              className="block"
            >
              <img
                src={wsibThumb}
                alt="WSIB clearance certificate"
                data-testid="wsib-thumb"
                className="h-16 w-24 rounded border border-gray-200 bg-gray-50 object-contain"
              />
            </button>
          ) : (
            <div className="flex h-16 w-24 items-center justify-center rounded border border-dashed border-gray-300 font-mono text-[9px] uppercase tracking-widest text-gray-400" data-testid="wsib-empty">
              No WSIB
            </div>
          )}
          <p className="font-mono text-[8px] uppercase tracking-widest text-gray-500" data-testid="wsib-caption">
            WSIB{wsib?.uploaded_at ? ` · ${fmtDate((wsib.uploaded_at || "").slice(0, 10))}` : ""}
          </p>
          <button
            onClick={() => wsibFileRef.current?.click()}
            disabled={wsibBusy}
            data-testid="wsib-upload-btn"
            title="Upload the newest WSIB clearance — replaces the current one and goes on every invoice"
            className="rounded bg-[#1a3fd1] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-[#1633b8] disabled:opacity-50"
          >
            {wsibBusy ? "Uploading…" : wsib?.has_image ? "Replace" : "Upload"}
          </button>
          <input
            ref={wsibFileRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="wsib-upload-input"
            onChange={(e) => uploadWsib(e.target.files?.[0])}
          />
        </div>
      </div>

      {/* Header stats — same card style as the Home tab */}
      {overview.stats && (
        <div className="mx-auto mb-4 grid max-w-[1400px] grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="billing-stats">
          <BillingStat
            testid="billing-stat-unbilled"
            label="Total unbilled"
            value={money(overview.stats.unbilled_recommended)}
            sub={`cost ${money(overview.stats.unbilled_cost)} · ${overview.stats.unbilled_items} item${overview.stats.unbilled_items === 1 ? "" : "s"}`}
            tint="border-[#e6dc7a] bg-[#fff8c2]/70"
          />
          <BillingStat
            testid="billing-stat-amount-due"
            label="Amount due"
            value={money(overview.stats.amount_due)}
            sub={`${overview.stats.unpaid_count} unpaid invoice${overview.stats.unpaid_count === 1 ? "" : "s"} · from QuickBooks`}
            tint="border-amber-300 bg-amber-50"
            onClick={() => setReceivablesOpen(true)}
            hint="Click for who owes what"
          />
          <BilledCard stats={overview.stats} onOpen={drillToAuditYear} />
          <NextPaymentsCard stats={overview.stats} onOpen={() => setReceivablesOpen(true)} />
        </div>
      )}

      {/* To be invoiced — projects with unbilled current-period hours/expenses */}
      <section className="mx-auto mb-4 max-w-[1400px] rounded-lg border-2 border-[#e6dc7a] bg-[#fff8c2] p-4 shadow-sm" data-testid="to-be-invoiced-box">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-[#a06400]">To be invoiced</h2>
          <div className="flex items-center gap-3">
            {overview.to_be_invoiced.length > 0 && (
              <button
                onClick={() => setConfirmAll(true)}
                data-testid="invoice-all-btn"
                className="inline-flex items-center gap-1 rounded bg-[#2ca01c] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-[#248015]"
              >
                <Mail className="h-3 w-3" /> Invoice all
              </button>
            )}
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#a06400]/70" data-testid="to-be-invoiced-count">{overview.to_be_invoiced.length} project{overview.to_be_invoiced.length === 1 ? "" : "s"}</span>
          </div>
        </div>
        {loading ? (
          <p className="py-4 text-center text-xs text-[#8a5600]">Loading…</p>
        ) : overview.to_be_invoiced.length === 0 ? (
          <p className="py-4 text-center text-xs text-[#8a5600]">Nothing waiting to be invoiced. Log hours or expenses on a project and it shows up here.</p>
        ) : (
          <div className="space-y-2">
            {cappedTbi.map((r) => (
              <div key={r.project_id}>
                <button
                  onClick={() => setInvoice({ projectId: r.project_id })}
                  data-testid={`to-invoice-${r.project_id}`}
                  className="flex w-full items-center justify-between gap-3 rounded-md border border-[#e6dc7a] bg-white px-3 py-2.5 text-left transition-shadow hover:shadow-md"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-800">{r.name}</p>
                    <p className="truncate text-xs text-gray-500">
                      {r.customer || "—"} · {r.item_count > 0 ? `${r.item_count} unbilled item${r.item_count === 1 ? "" : "s"}` : "no unbilled hours/expenses"}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="font-mono text-sm font-bold text-gray-900">{money(r.current_total)}</p>
                    {r.item_count > 0 && <p className="font-mono text-[10px] text-[#a06400]">recommended {money(r.recommended)}</p>}
                  </div>
                </button>
                {/* Extras marked "Include" — billed separately under the parent */}
                {(r.extras || []).length > 0 && (
                  <div className="ml-6 mt-1 space-y-1 border-l-2 border-[#e6dc7a] pl-3">
                    {r.extras.map((x) => (
                      <button
                        key={x.extra_id}
                        onClick={() => setInvoice({ projectId: r.project_id, extraId: x.extra_id })}
                        data-testid={`to-invoice-extra-${x.extra_id}`}
                        className="flex w-full items-center justify-between gap-3 rounded-md border border-[#e6dc7a]/70 bg-[#fffdf0] px-3 py-2 text-left transition-shadow hover:shadow-md"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-gray-700">↳ {x.estimate_number ? `Est #${x.estimate_number} · ` : ""}{x.item}</p>
                          <p className="truncate text-[11px] text-gray-500">{x.description || "Additional work"}</p>
                        </div>
                        <p className="shrink-0 font-mono text-xs font-bold text-gray-800">{money(x.amount)}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {tbiRows.length > 3 && (
              <button
                onClick={() => setTbiExpanded((v) => !v)}
                data-testid="to-invoice-show-more"
                className="w-full rounded-md border border-[#e6dc7a] bg-[#fff8c2]/60 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-700 transition-colors hover:bg-[#fff8c2]"
              >
                {tbiExpanded ? "Show less" : `Show more (${tbiRows.length - 3} more)`}
              </button>
            )}
          </div>
        )}
      </section>

      {/* Invoices sent — projects with billed draws */}
      <section className="mx-auto mb-6 max-w-[1400px] rounded-lg border-2 border-emerald-300 bg-emerald-50 p-4 shadow-sm" data-testid="invoices-sent-box">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-700">Invoices sent</h2>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={sentYear}
              onChange={(e) => setSentYear(e.target.value)}
              data-testid="invoices-sent-year-filter"
              className="rounded border border-emerald-300 bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 outline-none focus:border-emerald-500"
            >
              <option value="all">- All -</option>
              {sentYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              data-testid="invoices-sent-sort"
              className="rounded border border-emerald-300 bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 outline-none focus:border-emerald-500"
            >
              <option value="recent">Most recent</option>
              <option value="due">Due date</option>
              <option value="date">Invoice date</option>
              <option value="amount">Amount</option>
            </select>
            <button
              onClick={syncQbo}
              disabled={syncing}
              data-testid="qbo-invoice-sync-btn"
              title="Pull invoice data from QuickBooks (adds/refreshes QBO rows only — existing invoices are never overwritten)"
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-emerald-600 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-emerald-700 disabled:opacity-50"
            >
              <RefreshCcw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync QBO"}
            </button>
            <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-widest text-emerald-700/70" data-testid="invoices-sent-count">
              {sortedSent.length - paidSentCount} unpaid · {paidSentCount} paid
            </span>
          </div>
        </div>
        {overview.stats?.qbo_last_sync && (
          <p className="-mt-2 mb-3 text-right font-mono text-[9px] uppercase tracking-widest text-emerald-700/50" data-testid="qbo-last-sync">
            Last synced from QuickBooks at {fmtSyncTime(overview.stats.qbo_last_sync)}
          </p>
        )}
        {loading ? (
          <p className="py-4 text-center text-xs text-emerald-800">Loading…</p>
        ) : (overview.invoices_sent || []).length === 0 ? (
          <p className="py-4 text-center text-xs text-emerald-800">No draws billed yet. Mark rows billed on a project and it lands here.</p>
        ) : sortedSent.length === 0 ? (
          <p className="py-4 text-center text-xs text-emerald-800" data-testid="invoices-sent-empty-year">No invoices in {sentYear === "all" ? "any year" : sentYear} — pick another year or - All -.</p>
        ) : (
          <div className="space-y-2">
            {visibleSent.length === 0 && (
              <p className="py-3 text-center text-xs text-emerald-800" data-testid="invoices-sent-all-paid">Every invoice here is paid — nothing outstanding.</p>
            )}
            {cappedSent.map((r) => (
              <div key={r.source === "qbo" ? `qbo-${r.qbo_id}` : r.project_id}>
                {r.source === "qbo" ? (
                  <QboInvoiceRow r={r} onOpen={r.project_id ? () => setInvoice({ projectId: r.project_id }) : null} onMarkedPaid={load} />
                ) : (
                <>
                <div className="flex items-stretch gap-2">
                <button
                  onClick={() => setInvoice({ projectId: r.project_id })}
                  data-testid={`invoice-sent-${r.project_id}`}
                  className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-md border border-emerald-200 bg-white px-3 py-2.5 text-left transition-shadow hover:shadow-md"
                >
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 truncate text-sm font-semibold text-gray-800">
                      {r.name}
                      <span
                        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-300 bg-emerald-100 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider text-emerald-800"
                        title="App-generated invoice — stored only in this app, not in QuickBooks"
                        data-testid={`app-invoice-badge-${r.project_id}`}
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> App
                      </span>
                      {r.reply_pending && (
                        <span
                          role="button"
                          tabIndex={0}
                          title="Open the conversation in Mail"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchParams({ tab: "email", thread: r.last_invoice_subject || `Invoice — ${r.name}` });
                          }}
                          onKeyDown={(e) => e.key === "Enter" && e.stopPropagation()}
                          className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-[#1a3fd1] px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-[#ffffff] hover:bg-[#1633b8]"
                          data-testid={`reply-badge-${r.project_id}`}
                        >
                          <Mail className="h-2.5 w-2.5" /> Reply
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-gray-500">{r.customer || "—"} · {r.draw_count} draw{r.draw_count === 1 ? "" : "s"} · last {fmtDate(r.last_billed_at)}</p>
                  </div>
                  <p className="shrink-0 font-mono text-sm font-bold text-emerald-800">{r.billed_total > 0 ? money(r.billed_total) : "—"}</p>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleProjectPaid(r); }}
                  data-testid={`project-mark-paid-${r.project_id}`}
                  title={r.paid ? "Mark unpaid" : "Mark paid (this app's record — individual QuickBooks invoices keep their own status)"}
                  className={r.paid
                    ? "shrink-0 self-center whitespace-nowrap rounded border border-gray-300 bg-gray-50 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-gray-500 transition-colors hover:bg-gray-100"
                    : "shrink-0 self-center whitespace-nowrap rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-700 transition-colors hover:bg-emerald-100"}
                >
                  {r.paid ? "Mark unpaid" : "Mark paid"}
                </button>
                {user?.role === "superadmin" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}
                    data-testid={`invoice-delete-${r.project_id}`}
                    title="Delete this app-generated invoice record — labour, materials and extras go back to unbilled. QuickBooks invoices are never deleted here."
                    className="shrink-0 self-center rounded border border-red-200 bg-red-50 p-1.5 text-red-500 transition-colors hover:border-red-400 hover:bg-red-100 hover:text-red-700"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
                </div>
                {/* Billed extras — their own draw entries under the parent */}
                {(r.extras || []).length > 0 && (
                  <div className="ml-6 mt-1 space-y-1 border-l-2 border-emerald-300 pl-3">
                    {r.extras.map((x) => (
                      <button
                        key={x.extra_id}
                        onClick={() => setInvoice({ projectId: r.project_id, extraId: x.extra_id })}
                        data-testid={`invoice-sent-extra-${x.extra_id}`}
                        className="flex w-full items-center justify-between gap-3 rounded-md border border-emerald-200/70 bg-[#f2fbf4] px-3 py-2 text-left transition-shadow hover:shadow-md"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold text-gray-700">↳ {x.estimate_number ? `Est #${x.estimate_number} · ` : ""}{x.item}</p>
                          <p className="truncate text-[11px] text-gray-500">{x.description || "Additional work"} · billed {fmtDate(x.billed_at)}</p>
                        </div>
                        <p className="shrink-0 font-mono text-xs font-bold text-emerald-800">{money(x.amount)}</p>
                      </button>
                    ))}
                  </div>
                )}
                </>
                )}
              </div>
            ))}
            {(visibleSent.length > 3 || hiddenPaidCount > 0 || sentExpanded) && (
              <button
                onClick={() => { if (sentExpanded) setShowPaid(false); setSentExpanded(!sentExpanded); }}
                data-testid="invoices-sent-show-more-rows"
                className="w-full rounded-md border border-emerald-200 bg-emerald-100/60 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 transition-colors hover:bg-emerald-100"
              >
                {sentExpanded ? "Show less" : `Show more (${Math.max(visibleSent.length - 3, 0) + (!showPaid ? hiddenPaidCount : 0)} more)`}
              </button>
            )}
            {sentExpanded && (hiddenPaidCount > 0 || showPaid) && (
              <button
                onClick={() => setShowPaid((v) => !v)}
                data-testid="invoices-sent-show-more"
                className="w-full rounded-md border border-emerald-200 bg-emerald-100/60 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 transition-colors hover:bg-emerald-100"
              >
                {showPaid ? "Show less" : `Show ${hiddenPaidCount} paid invoice${hiddenPaidCount === 1 ? "" : "s"}`}
              </button>
            )}
          </div>
        )}
      </section>

      {/* Confirm delete of an app-generated invoice record (superadmin) */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          data-testid="invoice-delete-confirm"
          onClick={(e) => e.target === e.currentTarget && !deleting && setDeleteTarget(null)}
        >
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <p className="flex items-center gap-2 text-sm font-bold text-gray-900">
              <Trash2 className="h-4 w-4 text-red-500" /> Delete app-generated invoice?
            </p>
            <p className="mt-2 text-xs leading-relaxed text-gray-600">
              <span className="font-semibold text-gray-800">{deleteTarget.name}</span>
              {" — "}{deleteTarget.draw_count} draw{deleteTarget.draw_count === 1 ? "" : "s"}
              {deleteTarget.billed_total > 0 ? ` · ${money(deleteTarget.billed_total)}` : ""}.
              Every billed labour hour, material and extra on this job goes back to
              <span className="font-semibold"> unbilled</span> so it shows up in To Be Invoiced
              and New Period again. The draw history and manual paid flag are cleared.
            </p>
            <p className="mt-2 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[10px] text-emerald-800">
              QuickBooks invoices are never deleted here — this only touches records stored in this app.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                data-testid="invoice-delete-cancel"
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700 hover:border-gray-400 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={doDeleteAppInvoice}
                disabled={deleting}
                data-testid="invoice-delete-confirm-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" /> {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice audit — flat, month-filterable report for the accountant */}
      <section ref={auditRef} className="mx-auto mb-6 max-w-[1400px] rounded-lg border border-gray-200 bg-white p-4 shadow-sm" data-testid="invoice-audit-box">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-gray-600">Invoice audit</h2>
          <div className="flex items-center gap-2">
            <select
              value={auditYear}
              onChange={(e) => setAuditYear(e.target.value)}
              data-testid="audit-year-filter"
              className="rounded border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-700 outline-none focus:border-[#1a3fd1]"
            >
              <option value="all">- All -</option>
              {auditYearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={auditMonthNum}
              onChange={(e) => setAuditMonthNum(e.target.value)}
              disabled={auditYear === "all"}
              data-testid="audit-month-filter"
              title={auditYear === "all" ? "Pick a year first to filter by month" : "Filter by month"}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-700 outline-none focus:border-[#1a3fd1] disabled:opacity-40"
            >
              <option value="">All months</option>
              {auditMonthsAvail.map((m) => (
                <option key={m} value={m}>{MONTH_SHORT[parseInt(m, 10) - 1]}</option>
              ))}
            </select>
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400" data-testid="audit-count">
              {audit ? `${audit.rows.length} invoice${audit.rows.length === 1 ? "" : "s"}` : "…"}
            </span>
          </div>
        </div>
        {audit === null ? (
          <p className="py-4 text-center text-xs text-gray-400">Loading…</p>
        ) : audit.rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-gray-400">No invoices{auditMonthNum ? " in this month" : auditYear !== "all" ? ` in ${auditYear}` : ""} yet.</p>
        ) : (
          <>
            <div className="hidden grid-cols-12 gap-2 border-b border-gray-200 px-3 pb-2 font-mono text-[9px] uppercase tracking-widest text-gray-400 sm:grid">
              <span className="col-span-3">Invoice #</span>
              <span className="col-span-3">Customer</span>
              <span className="col-span-2">Project</span>
              <span className="col-span-2">Date · due</span>
              <span className="col-span-2 text-right">Amount</span>
            </div>
            <div className="divide-y divide-gray-100">
              {(auditExpanded ? audit.rows : audit.rows.slice(0, 3)).map((r, i) => (
                <div key={`${r.source}-${r.invoice_no}-${i}`} className="grid grid-cols-1 gap-1 px-3 py-2.5 sm:grid-cols-12 sm:items-center sm:gap-2" data-testid={`audit-row-${r.source}-${r.invoice_no}`}>
                  <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-sm font-semibold text-gray-800 sm:col-span-3">
                    <span className="shrink-0">#{r.invoice_no}</span>
                    <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider ${r.source === "qbo" ? "bg-emerald-100 text-emerald-700" : "bg-[#1a3fd1]/10 text-[#1a3fd1]"}`}>
                      {r.source === "qbo" ? "QBO" : "App"}
                    </span>
                    {r.status === "unpaid" && (
                      <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-amber-700">Unpaid</span>
                    )}
                  </p>
                  <p className="truncate text-xs text-gray-600 sm:col-span-3">{r.customer || "—"}</p>
                  <p className="truncate text-xs text-gray-500 sm:col-span-2">{r.project || r.label || "—"}</p>
                  <p className="font-mono text-[11px] text-gray-500 sm:col-span-2">
                    <span className="whitespace-nowrap">{fmtDate(r.date)}</span>
                    {r.due_date && <span className="block whitespace-nowrap text-gray-400">due {fmtDate(r.due_date)}</span>}
                  </p>
                  <p className="font-mono text-sm font-bold text-gray-900 sm:col-span-2 sm:text-right">{money(r.amount)}</p>
                </div>
              ))}
            </div>
            {audit.rows.length > 3 && (
              <button
                onClick={() => setAuditExpanded((v) => !v)}
                data-testid="audit-show-more"
                className="mt-2 w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-600 transition-colors hover:bg-gray-100"
              >
                {auditExpanded ? "Show less" : `Show more (${audit.rows.length - 3} more)`}
              </button>
            )}
            <div className="mt-2 flex justify-end border-t border-gray-200 pt-2">
              <p className="font-mono text-xs font-bold text-gray-800" data-testid="audit-total">
                Total {money(audit.rows.reduce((s, r) => s + (r.amount || 0), 0))}
              </p>
            </div>
          </>
        )}
      </section>

      {invoice && (
        <InvoiceModal
          projectId={invoice.projectId}
          extraId={invoice.extraId}
          onClose={() => setInvoice(null)}
        />
      )}

      {receivablesOpen && overview.stats && (
        <ReceivablesModal
          stats={overview.stats}
          onClose={() => { setReceivablesOpen(false); setReceivablesFocus(null); }}
          onReminded={load}
          focusCustomer={receivablesFocus}
          onClearFocus={() => setReceivablesFocus(null)}
        />
      )}

      {/* Invoice-all confirmation — lists the QuickBooks email per invoice */}
      {confirmAll && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          data-testid="invoice-all-confirm"
          onClick={(e) => e.target === e.currentTarget && setConfirmAll(false)}
        >
          <div className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
            <p className="text-sm font-bold text-gray-900">Send all invoices?</p>
            <label className="mb-1 mt-3 block font-mono text-[10px] uppercase tracking-widest text-gray-500">From</label>
            <SenderPicker value={senderAll} onChange={setSenderAll} testid="invoice-all-sender" />
            <p className="mt-3 text-xs text-gray-500">Each invoice is emailed to the address on file in QuickBooks:</p>
            <div className="mt-3 space-y-1.5">
              {overview.to_be_invoiced.flatMap((r) => [
                <div key={r.project_id} className="flex items-center justify-between gap-2 rounded bg-gray-50 px-3 py-2 text-xs">
                  <span className="truncate font-semibold text-gray-800">{r.name}</span>
                  <span className={`shrink-0 font-mono ${r.customer_email ? "text-gray-700" : "text-red-600"}`} data-testid={`invoice-all-email-${r.project_id}`}>
                    {r.customer_email || "no email"}
                  </span>
                </div>,
                ...(r.extras || []).map((x) => (
                  <div key={x.extra_id} className="ml-4 flex items-center justify-between gap-2 rounded bg-gray-50 px-3 py-2 text-xs">
                    <span className="truncate text-gray-700">↳ {x.item}</span>
                    <span className={`shrink-0 font-mono ${r.customer_email ? "text-gray-700" : "text-red-600"}`}>{r.customer_email || "no email"}</span>
                  </div>
                )),
              ])}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setConfirmAll(false)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={invoiceAll}
                disabled={!senderAll || sendingAll}
                data-testid="invoice-all-confirm-btn"
                className="rounded-md bg-[#1a3fd1] px-4 py-1.5 text-xs font-semibold text-[#ffffff] hover:bg-[#1633b8] disabled:opacity-40"
              >
                {sendingAll ? "Sending…" : "Send all"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingTab;
